/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileSearch,
  Loader2,
  RotateCcw,
  Search,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DetailedPoliceReport,
  MatterInvestigationStatus,
} from '@/services/api/generated/policeReportsService';
import {
  usePoliceInvestigationListQuery,
  usePoliceInvestigationPartialUpdateMutation,
} from '@/services/api/policeInvestigationService';
import { enhancedPoliceReportsApi } from '@/services/api/policeReportsService';

interface InvestigationPizzaTrackerProps {
  matterId: string;
  policeReports: DetailedPoliceReport[];
  dateOfLoss?: string | null;
  isLoading?: boolean;
}

const InvestigationPizzaTrackerSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200">
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-3">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <Skeleton className="w-5 h-5 rounded" />
          <div>
            <Skeleton className="h-4 w-32 mb-1" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-40" />
        </div>
      </div>
    </div>
  </div>
);

const STATUS_CONFIG: Record<
  MatterInvestigationStatus,
  { label: string; description: string; icon: any; color: string }
> = {
  need_info: {
    label: 'Awaiting Info',
    description: 'Waiting for client details',
    icon: Clock,
    color: 'text-orange-700',
  },
  researching: {
    label: 'Researching',
    description: 'Finding the right department',
    icon: Search,
    color: 'text-blue-700',
  },
  investigating: {
    label: 'Investigating',
    description: 'Verifying report availability',
    icon: FileSearch,
    color: 'text-indigo-700',
  },
  escalated: {
    label: 'Needs Attention',
    description: 'Requires manual review',
    icon: AlertTriangle,
    color: 'text-amber-700',
  },
  completed_report_received: {
    label: 'Complete',
    description: 'Police report received',
    icon: CheckCircle2,
    color: 'text-green-700',
  },
  closed_no_report: {
    label: 'Closed',
    description: 'No report available',
    icon: XCircle,
    color: 'text-gray-600',
  },
  not_applicable: {
    label: 'N/A',
    description: 'Investigation not needed',
    icon: XCircle,
    color: 'text-gray-600',
  },
  backfilled_closed: {
    label: 'Closed',
    description: 'Previously closed',
    icon: XCircle,
    color: 'text-gray-600',
  },
};

const isTerminal = (status: MatterInvestigationStatus | null | undefined) => {
  if (!status) return false;
  return (
    status === 'completed_report_received' ||
    status === 'closed_no_report' ||
    status === 'not_applicable' ||
    status === 'backfilled_closed'
  );
};

export const InvestigationPizzaTracker = ({
  matterId,
  policeReports,
  dateOfLoss,
  isLoading: isParentLoading = false,
}: InvestigationPizzaTrackerProps) => {
  const {
    data: investigationData,
    isLoading: isInvestigationLoading,
    refetch,
  } = usePoliceInvestigationListQuery({ matterId }, { skip: !matterId });

  // Backend returns single object (1-to-1 with matter), but codegen thinks it's array
  // Cast to handle both cases safely
  const investigation = Array.isArray(investigationData)
    ? investigationData[0]
    : investigationData;

  const [updateInvestigation, { isLoading: isUpdating }] =
    usePoliceInvestigationPartialUpdateMutation();
  const [updateReportStatus, { isLoading: isUpdatingReport }] =
    enhancedPoliceReportsApi.usePoliceReportsPartialUpdateMutation();
  const [confirmationState, setConfirmationState] = useState<{
    isActive: boolean;
    type: 'complete' | 'close' | 'reopen' | null;
    comment: string;
    selectedReportId?: string;
  }>({
    isActive: false,
    type: null,
    comment: '',
    selectedReportId: undefined,
  });

  // Calculate days since date of loss
  const daysSinceDateOfLoss = useMemo(() => {
    if (!dateOfLoss) return null;
    const dol = new Date(dateOfLoss);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - dol.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, [dateOfLoss]);

  const handleStatusUpdate = async (
    status: MatterInvestigationStatus,
    note?: string,
  ) => {
    if (!investigation?.id) return;

    try {
      await updateInvestigation({
        matterId,
        id: investigation.id,
        patchedUpdateMatterInvestigation: {
          id: investigation.id,
          status,
          description: note?.trim() || investigation.description,
        },
      }).unwrap();

      const messages: Record<MatterInvestigationStatus, string> = {
        completed_report_received: 'Investigation completed - report received',
        closed_no_report: 'Investigation closed - no report available',
        researching: 'Investigation reopened',
        need_info: 'Investigation updated',
        investigating: 'Investigation updated',
        escalated: 'Investigation updated',
        not_applicable: 'Investigation updated',
        backfilled_closed: 'Investigation updated',
      };

      toast.success(messages[status]);
      setConfirmationState({
        isActive: false,
        type: null,
        comment: '',
        selectedReportId: undefined,
      });
      await refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      toast.error('Update failed');
    }
  };

  const startConfirmation = (type: 'complete' | 'close' | 'reopen') => {
    setConfirmationState({
      isActive: true,
      type,
      comment: '',
      selectedReportId: undefined,
    });
  };

  const cancelConfirmation = () => {
    setConfirmationState({
      isActive: false,
      type: null,
      comment: '',
      selectedReportId: undefined,
    });
  };

  const confirmAction = async () => {
    const { type, comment, selectedReportId } = confirmationState;

    if (type === 'complete') {
      // If we have a selected report, mark it as received first
      if (selectedReportId) {
        try {
          await updateReportStatus({
            id: selectedReportId,
            matterId,
            patchedUpdatePoliceReport: {
              id: selectedReportId,
              attestation_status: 'verified_received',
            },
          }).unwrap();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to mark report as received:', error);
          toast.error('Failed to mark report as received');
          return;
        }
      }

      handleStatusUpdate('completed_report_received', comment);
    } else if (type === 'close') {
      handleStatusUpdate('closed_no_report', comment);
    } else if (type === 'reopen') {
      handleStatusUpdate('researching', comment);
    }
  };

  const getConfirmationConfig = (
    type: 'complete' | 'close' | 'reopen' | null,
  ) => {
    const configs = {
      complete: {
        title: 'Mark Investigation Complete',
        description: 'Confirm that the police report has been received',
        placeholder: 'Add any notes about the received report...',
        confirmText: 'Mark Complete',
        confirmColor: 'green' as const,
      },
      close: {
        title: 'Close Investigation',
        description: 'Confirm that no police report is available',
        placeholder: 'Explain why no report is available...',
        confirmText: 'Close Investigation',
        confirmColor: 'red' as const,
      },
      reopen: {
        title: 'Reopen Investigation',
        description: 'Restart the investigation process',
        placeholder: 'Reason for reopening the investigation...',
        confirmText: 'Reopen Investigation',
        confirmColor: 'blue' as const,
      },
    };
    return type ? configs[type] : null;
  };

  if (isParentLoading || isInvestigationLoading) {
    return <InvestigationPizzaTrackerSkeleton />;
  }

  if (!investigation) return null;

  const current = investigation.status as MatterInvestigationStatus;
  const statusConfig = STATUS_CONFIG[current];
  const canReopen = isTerminal(current);
  const Icon = statusConfig?.icon || Clock;

  const getDaysColor = (days: number) => {
    if (days >= 30) return 'text-red-600 font-semibold';
    if (days >= 14) return 'text-orange-600 font-medium';
    return 'text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Police Report Status
          </div>
          {daysSinceDateOfLoss !== null && !isTerminal(current) && (
            <div className={`text-xs ${getDaysColor(daysSinceDateOfLoss)}`}>
              {daysSinceDateOfLoss} days since DOL
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon
              className={`w-5 h-5 ${statusConfig?.color || 'text-gray-500'}`}
            />
            <div>
              <div className="text-sm font-medium text-gray-900">
                {statusConfig?.label || 'Unknown'}
              </div>
              <div className="text-xs text-gray-500">
                {investigation.description || statusConfig?.description || ''}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            {!isTerminal(current) && (
              <>
                <Button
                  outline
                  onClick={() => startConfirmation('complete')}
                  disabled={isUpdating || confirmationState.isActive}
                  className="text-xs"
                >
                  <CheckCircle2 data-slot="icon" className="w-4 h-4" />
                  Report Received
                </Button>
                <Button
                  plain
                  onClick={() => startConfirmation('close')}
                  disabled={isUpdating || confirmationState.isActive}
                  className="text-xs"
                >
                  <XCircle data-slot="icon" className="w-4 h-4" />
                  No Report Available
                </Button>
              </>
            )}
            {canReopen && (
              <Button
                outline
                onClick={() => startConfirmation('reopen')}
                disabled={isUpdating}
                className="text-xs"
              >
                <RotateCcw data-slot="icon" className="w-4 h-4" />
                Reopen
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Inline Confirmation */}
      {confirmationState.isActive && (
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
          <div className="space-y-4">
            {(() => {
              const config = getConfirmationConfig(confirmationState.type);
              if (!config) return null;

              return (
                <>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {config.title}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {config.description}
                    </p>
                  </div>

                  {/* Report Selection for Complete Action */}
                  {confirmationState.type === 'complete' &&
                    policeReports &&
                    !policeReports.some(
                      (entry) =>
                        entry.attestation_status === 'verified_received',
                    ) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Select which report to mark as received:
                        </label>
                        <select
                          value={confirmationState.selectedReportId || ''}
                          onChange={(e) =>
                            setConfirmationState((prev) => ({
                              ...prev,
                              selectedReportId: e.target.value || undefined,
                            }))
                          }
                          className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                        >
                          <option value="">Choose a department...</option>
                          {policeReports
                            .filter(
                              (entry) =>
                                entry.attestation_status ===
                                  'attested_verified' ||
                                entry.attestation_status ===
                                  'attested_unverified' ||
                                entry.attestation_status === 'inferred',
                            )
                            .map((report) => (
                              <option key={report.id} value={report.id}>
                                {report.police_department.name ||
                                  'Unknown Department'}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}

                  <div>
                    <textarea
                      value={confirmationState.comment}
                      onChange={(e) =>
                        setConfirmationState((prev) => ({
                          ...prev,
                          comment: e.target.value,
                        }))
                      }
                      placeholder={config.placeholder}
                      className="w-full text-sm border border-gray-300 rounded px-3 py-2 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      outline
                      onClick={cancelConfirmation}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                    <Button
                      color={config.confirmColor}
                      onClick={confirmAction}
                      disabled={
                        isUpdating ||
                        isUpdatingReport ||
                        (confirmationState.type === 'complete' &&
                          policeReports &&
                          !policeReports.some(
                            (entry) =>
                              entry.attestation_status === 'verified_received',
                          ) &&
                          !confirmationState.selectedReportId)
                      }
                    >
                      {isUpdating && (
                        <Loader2 data-slot="icon" className="animate-spin" />
                      )}
                      {!isUpdating && confirmationState.type === 'complete' && (
                        <CheckCircle2 data-slot="icon" />
                      )}
                      {!isUpdating && confirmationState.type === 'close' && (
                        <XCircle data-slot="icon" />
                      )}
                      {!isUpdating && confirmationState.type === 'reopen' && (
                        <RotateCcw data-slot="icon" />
                      )}
                      {config.confirmText}
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};
